import { lazy, Suspense, useEffect, useState, useRef, ComponentType } from "react";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";

// Lazy load below-the-fold sections
const Services = lazy(() => import("@/components/Services"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const NoComplaints = lazy(() => import("@/components/NoComplaints"));
const Certifications = lazy(() => import("@/components/Certifications"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Contact = lazy(() => import("@/components/Contact"));
const Map = lazy(() => import("@/components/Map"));
const Partners = lazy(() => import("@/components/Partners"));

// Intersection Observer based lazy section
const LazySection = ({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ contentVisibility: 'auto', containIntrinsicSize: '0 400px' }}>
      {isVisible ? (
        <Suspense fallback={fallback || <div className="min-h-[200px]" />}>
          {children}
        </Suspense>
      ) : (
        <div className="min-h-[200px]" />
      )}
    </div>
  );
};

const Index = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>EW Saúde Ambiental | Dedetização em Pernambuco</title>
        <meta name="description" content="Empresa especializada em dedetização, desratização, descupinização e controle de pragas em Pernambuco. Atendimento rápido com garantia." />
        <link rel="canonical" href="https://ewsaudeambiental.com/" />
        <meta property="og:title" content="EW Saúde Ambiental | Dedetização e Controle de Pragas em PE" />
        <meta property="og:description" content="Empresa referência em controle de pragas em Pernambuco. Solicite orçamento." />
        <meta property="og:image" content="https://ewsaudeambiental.com/og-image.png" />
        <meta property="og:url" content="https://ewsaudeambiental.com/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "EW Saúde Ambiental",
            "description": "Empresa especializada em controle de pragas urbanas em Pernambuco desde 2015. Dedetização, desratização, descupinização e sanitização com produtos aprovados pela ANVISA e garantia de serviço.",
            "url": "https://ewsaudeambiental.com",
            "telephone": "+55-81-99134-3181",
            "email": "comercialewsaudeambiental@gmail.com",
            "image": "https://ewsaudeambiental.com/og-image.png",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Travessa 1º Tiradentes, Lagoa do Sapo",
              "addressLocality": "Feira Nova",
              "addressRegion": "PE",
              "postalCode": "55715-000",
              "addressCountry": "BR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": -7.9469,
              "longitude": -35.3928
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
                "opens": "08:00",
                "closes": "17:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "08:00",
                "closes": "12:00"
              }
            ],
            "makesOffer": [
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dedetização de Baratas"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dedetização de Formigas"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dedetização de Escorpiões"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dedetização de Mosquitos"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Desratização"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Descupinização"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Controle de Morcegos"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Sanitização de Ambientes"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Limpeza de Caixa d'Água"}},
              {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Dedetização de Pulgas e Carrapatos"}}
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "127",
              "bestRating": "5"
            },
            "areaServed": [
              "Recife", "Olinda", "Caruaru", "Jaboatão dos Guararapes",
              "Paulista", "Cabo de Santo Agostinho", "Camaragibe",
              "São Lourenço da Mata", "Igarassu", "Abreu e Lima",
              "Vitória de Santo Antão", "Gravatá", "Surubim",
              "Carpina", "Paudalho", "Limoeiro", "Goiana",
              "Feira Nova", "Pernambuco"
            ],
            "sameAs": [
              "https://www.instagram.com/ewsaudeambiental",
              "https://www.tiktok.com/@ewsaudeambiental"
            ],
            "foundingDate": "2015",
            "numberOfEmployees": {
              "@type": "QuantitativeValue",
              "minValue": 10,
              "maxValue": 50
            },
            "hasCredential": [
              {"@type": "EducationalOccupationalCredential", "credentialCategory": "Licença CPRH"},
              {"@type": "EducationalOccupationalCredential", "credentialCategory": "Registro ADAGRO"},
              {"@type": "EducationalOccupationalCredential", "credentialCategory": "Registro CREA-PE"},
              {"@type": "EducationalOccupationalCredential", "credentialCategory": "Licença Vigilância Sanitária"}
            ]
          }
        `}</script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <LazySection>
          <Services />
        </LazySection>
        <LazySection>
          <Testimonials />
        </LazySection>
        <LazySection>
          <NoComplaints />
        </LazySection>
        <LazySection>
          <Certifications />
        </LazySection>
        <LazySection>
          <FAQ />
        </LazySection>
        <LazySection>
          <Contact />
        </LazySection>
        <LazySection>
          <Map />
        </LazySection>
        <LazySection>
          <Partners />
        </LazySection>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Index;
